import React, { useEffect, useState } from 'react'
import { Modal, View, ActivityIndicator, StyleSheet } from 'react-native'
import { Button, Text, useTheme } from 'react-native-paper'
import Toast from 'react-native-root-toast'
import { useTranslation } from 'react-i18next'
import { PaymentTimer } from './payment-timer.component'
import { useGetBillingStatusQuery } from '../../../generated/graphql'

interface PaymentConfirmationModalProps {
  visible: boolean
  billingId: string
  onSuccess: () => void
  onError: () => void
  onClose: () => void
}

interface PaymentStatus {
  status: string
  message?: string | null
  paidAt?: any
  processedAt?: any
}

export const PaymentConfirmationModal: React.FC<
  PaymentConfirmationModalProps
> = ({ visible, billingId, onSuccess, onError, onClose }) => {
  const theme = useTheme()
  const { t } = useTranslation('payment')
  const [timeLeft, setTimeLeft] = useState(60)
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null)
  const { data } = useGetBillingStatusQuery({
    variables: { id: billingId },
    skip: !visible || !billingId,
    pollInterval: visible && billingId ? 5000 : 0, // Poll every 5 seconds when visible
  })

  // Countdown timer
  useEffect(() => {
    if (!visible || !billingId) return

    setTimeLeft(60)

    const countdownInterval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval)
          handleTimeout()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      clearInterval(countdownInterval)
    }
  }, [visible, billingId])

  // Update payment status when GraphQL data changes
  useEffect(() => {
    if (data?.billingStatus) {
      const status: PaymentStatus = data.billingStatus
      setPaymentStatus(status)

      if (status.status === 'completed') {
        Toast.show(t('payment-confirmed-success'), {
          position: Toast.positions.CENTER,
        })
        onSuccess()
        return
      }

      if (status.status === 'failed') {
        Toast.show(t('payment-failed'), {
          position: Toast.positions.CENTER,
        })
        onError()
        return
      }
    }
  }, [data, onSuccess, onError, t])

  const handleTimeout = () => {
    Toast.show(t('payment-timeout'), {
      position: Toast.positions.CENTER,
    })
    onError()
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <Text variant="titleLarge" style={styles.title}>
            {t('confirming-payment')}
          </Text>

          <View style={styles.statusContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text variant="bodyLarge" style={{ textAlign: 'center' }}>
              {paymentStatus?.status === 'paid'
                ? t('processing-payment')
                : t('waiting-confirmation')}
            </Text>
          </View>

          <PaymentTimer timeLeft={timeLeft} />

          <Button
            mode="contained"
            onPress={onClose}
            buttonColor={theme.colors.error}
            style={styles.cancelButton}
          >
            {t('cancel')}
          </Button>
        </View>
      </View>
    </Modal>
  )
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 24,
    width: '80%',
    maxWidth: 400,
    alignItems: 'center',
  },
  title: {
    marginBottom: 20,
    textAlign: 'center',
  },
  statusContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },

  paymentStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  statusLabel: {
    marginRight: 8,
  },
  cancelButton: {
    minWidth: 120,
  },
})
